/** Round the clue appeared in. */
export type Round = 'J' | 'DJ' | 'FJ' | 'TB';

/** One historical Jeopardy clue. Field names kept short — 500k of these ship in JSON shards. */
export interface Clue {
  /** Global integer id; shard file = floor(id / SHARD_SIZE). */
  i: number;
  /** Original show category, e.g. "POTPOURRI". */
  c: string;
  /** Clue text (what the host reads — Jeopardy's "answer"). */
  q: string;
  /** Correct response, e.g. "What is Australia?". */
  a: string;
  /** Dollar value as aired (0 for Final Jeopardy / tiebreaker). */
  v: number;
  r: Round;
  /** Air date YYYY-MM-DD. */
  d: string;
  /** True if this was a Daily Double. */
  dd?: 1;
  /** Meta-tag ids (indexes into the tag table). */
  t: number[];
  /** Normalized difficulty 0..1 (value position within round, era-adjusted). */
  df: number;
}

export interface TagInfo {
  id: number;
  name: string;
  /** Number of clues carrying this tag. */
  count: number;
}

/** User's self-grade after reveal. */
export enum Grade {
  /** Red — missed it. */
  Missed = 0,
  /** Light green — got it, but shaky. */
  Shaky = 1,
  /** Dark green — knew it cold. */
  Known = 2,
}

/** Per-clue scheduling state (FSRS-style memory model). */
export interface CardState {
  clueId: number;
  /** Memory stability in days. */
  stability: number;
  /** Item difficulty 1..10 (FSRS scale). */
  difficulty: number;
  /** Epoch ms when this card is due for review. */
  due: number;
  /** Epoch ms of last review. */
  lastReview: number;
  reps: number;
  lapses: number;
  lastGrade: Grade;
}

/** Per-tag skill estimate (Elo-style rating with uncertainty). */
export interface TagSkill {
  tagId: number;
  /** Rating on the same 0..1 scale as clue difficulty (probability-of-knowing midpoint). */
  rating: number;
  /** Uncertainty 0..1 — high when few attempts; drives exploration. */
  uncertainty: number;
  attempts: number;
  /** Exponentially-weighted recent accuracy (Known=1, Shaky=0.6, Missed=0). */
  recentAccuracy: number;
}

export type PracticeMode =
  | { kind: 'tag'; tagId: number }
  | { kind: 'adaptive' } // all tags, weakness-targeted
  | { kind: 'category'; title: string } // one exact original show category
  | { kind: 'deck'; deckId: number; name: string }; // a curated focus deck

/** A reconstructed authentic board: one round of one aired episode. */
export interface BoardCategory {
  name: string;
  /** Up to 5 clues, ordered cheap → expensive. */
  clues: Clue[];
}
export interface Board {
  date: string;
  round: Round;
  categories: BoardCategory[];
}

export interface SessionStats {
  presented: number;
  known: number;
  shaky: number;
  missed: number;
}

/** What the UI renders at any moment. */
export type SessionPhase =
  | { phase: 'question'; clue: Clue }
  | { phase: 'reveal'; clue: Clue }
  | { phase: 'loading' }
  | { phase: 'done'; stats: SessionStats };
